import React, { useState } from 'react'
import { useCookieConsent, ConsentType } from '../../hooks/useCookieConsent'

interface CookieOption {
  type: ConsentType
  name: string
  description: string
}

const cookieOptions: CookieOption[] = [
  {
    type: 'google',
    name: 'Google',
    description: 'Allows embedded Google Forms, Google Calendar and YouTube to function and enables Google to collect analytics data.'
  },
  {
    type: 'microsoft',
    name: 'Microsoft',
    description: 'Allows embedded Microsoft Forms to function and enables Microsoft to collect telemetry data.'
  },
  {
    type: 'instagram',
    name: 'Instagram',
    description: 'Allows embedded Instagram posts to display and enables Meta to track your interactions.'
  }
]

/**
 * CookieSettings - Component for managing cookie consent preferences
 * 
 * Can be added to any page (e.g., a Privacy Policy page) to allow users
 * to review and change their cookie consent choices.
 */
const CookieSettings: React.FC = () => {
  const {
    hasConsent,
    hasDecided,
    acceptConsent,
    rejectConsent,
    clearAllConsents,
    isLoaded
  } = useCookieConsent()

  const [showConfirmation, setShowConfirmation] = useState(false)

  const handleToggle = (type: ConsentType, currentValue: boolean) => {
    if (currentValue) {
      rejectConsent(type)
    } else {
      acceptConsent(type)
    }
    setShowConfirmation(true)
    setTimeout(() => setShowConfirmation(false), 3000)
  }

  const handleAcceptAll = () => {
    acceptConsent(null)
    setShowConfirmation(true)
    setTimeout(() => setShowConfirmation(false), 3000)
  }

  const handleRejectAll = () => {
    rejectConsent(null)
    setShowConfirmation(true)
    setTimeout(() => setShowConfirmation(false), 3000)
  }

  const handleClearAll = () => {
    clearAllConsents()
    setShowConfirmation(true)
    setTimeout(() => setShowConfirmation(false), 3000)
  }

  if (!isLoaded) {
    return (
      <div className="my-6">
        <div className="bg-surface border border-border rounded-lg p-8">
          <div className="text-center text-text-light">Loading cookie preferences...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="my-6">
      <div className="bg-surface border border-border rounded-lg p-6 shadow-sm">
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-bold text-primary mb-2">
            Cookie Preferences
          </h2>
          <p className="text-text-light">
            Manage your consent for third-party cookies used on this site. 
            Changes take effect immediately and will apply to all embedded content.
          </p>
        </div>

        {showConfirmation && (
          <div className="mb-4 bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-center gap-3">
            <svg
              className="w-5 h-5 text-primary flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-primary font-medium">Your preferences have been saved.</span>
          </div>
        )}

        <div className="space-y-4 mb-6">
          {cookieOptions.map(option => {
            const isAccepted = hasConsent(option.type)
            const hasChoice = hasDecided(option.type)
            
            return (
              <div
                key={option.type}
                className="bg-background border border-border rounded-lg p-4"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-text mb-1">
                      {option.name}
                    </h3>
                    <p className="text-sm text-text-light">
                      {option.description}
                    </p>
                    {hasChoice && (
                      <div className="mt-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                          isAccepted 
                            ? 'bg-primary/10 text-primary' 
                            : 'bg-text-light/10 text-text-light'
                        }`}>
                          {isAccepted ? (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Accepted
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Rejected
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleToggle(option.type, isAccepted)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                      isAccepted ? 'bg-primary' : 'bg-border'
                    }`}
                    role="switch"
                    aria-checked={isAccepted}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isAccepted ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
          <button
            onClick={handleAcceptAll}
            className="btn btn-primary"
          >
            Accept All
          </button>
          <button
            onClick={handleRejectAll}
            className="btn btn-secondary"
          >
            Reject All
          </button>
          <button
            onClick={handleClearAll}
            className="btn btn-secondary"
          >
            Reset All
          </button>
        </div>
      </div>
    </div>
  )
}

export default CookieSettings
